import { useRef, type MouseEventHandler, type ReactNode } from 'react';
import type { FilteredRestaurant } from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import { cn } from '@discover-lib/utils';
import useDragToScroll from '@discover-lib/useDragToScroll';
import ScrollContainer from '@discover-components/availability/carousel/scrollContainer';
import Button from '@discover-ui/button';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import FilteredRestaurantCarouselCard from './card';

export const MIN_CAROUSEL_RESTAURANTS = 3;

export const CAROUSEL_ITEM_CLASS = cn(
  'xl:w-[28%] w-[67%] max-w-[270px] shrink-0 sm:w-[48%] md:w-[38%] lg:w-[32%]'
);

const HEADER_CLASS = 'mb-2 flex items-center justify-between gap-3 px-3 lg:mb-3 lg:pl-0 lg:pr-10';

const HEADING_CLASS = 'm-0 text-base font-semibold tracking-tight text-black';

const TRACK_CLASS = 'mr-2.5 items-stretch gap-2.5 px-3 md:px-0';

export type RestaurantCarouselFrameProps = {
  heading: ReactNode;
  seeAll: ReactNode;
  items: { key: string | number; node: ReactNode }[];
  scrollable?: boolean;
  headerClassName?: string;
  headingClassName?: string;
  trackClassName?: string;
  itemClassName?: string;
  className?: string;
  id?: string;
};

export function RestaurantCarouselFrame({
  heading,
  seeAll,
  items,
  scrollable = true,
  headerClassName,
  headingClassName,
  trackClassName,
  itemClassName,
  className,
  id,
}: RestaurantCarouselFrameProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isDragging, isScrollable, handleMouseDown, handleClickCapture } = useDragToScroll(
    scrollRef,
    { contentVersion: items.length }
  );

  return (
    <div id={id} className={cn('w-full', className)}>
      <div className={headerClassName ?? HEADER_CLASS}>
        <h2 className={headingClassName ?? HEADING_CLASS}>{heading}</h2>
        {seeAll}
      </div>
      <ScrollContainer
        ref={scrollRef}
        className={cn(
          trackClassName ?? TRACK_CLASS,
          scrollable && isScrollable && isDragging && 'cursor-grabbing',
          scrollable && isScrollable && !isDragging && 'cursor-grab',
          !scrollable && 'overflow-x-hidden'
        )}
        onMouseDownCapture={scrollable ? handleMouseDown : undefined}
        onClickCapture={scrollable ? handleClickCapture : undefined}>
        {items.map((item) => (
          <div key={item.key} className={itemClassName ?? CAROUSEL_ITEM_CLASS}>
            {item.node}
          </div>
        ))}
      </ScrollContainer>
    </div>
  );
}

export type RestaurantCarouselProps = {
  restaurants: FilteredRestaurant[];
  heading: ReactNode;
  seeAll: ReactNode;
  renderCard: (restaurant: FilteredRestaurant, index: number) => ReactNode;
  headerClassName?: string;
  headingClassName?: string;
  trackClassName?: string;
  itemClassName?: string;
  className?: string;
  id?: string;
  minRestaurants?: number;
};

export function RestaurantCarousel({
  restaurants,
  heading,
  seeAll,
  renderCard,
  minRestaurants = MIN_CAROUSEL_RESTAURANTS,
  ...frameProps
}: RestaurantCarouselProps) {
  if (restaurants.length < minRestaurants) return null;

  return (
    <RestaurantCarouselFrame
      {...frameProps}
      heading={heading}
      seeAll={seeAll}
      items={restaurants.map((restaurant, index) => ({
        key: restaurant.id,
        node: (
          <TrackProvider
            trackEvent="restaurant_impression"
            component_name="restaurant card"
            index={index}
            component_detail={restaurant.menuTitle}
            component_id={restaurant.id}
            eventParams={{
              source_page_id: restaurant.id,
              action: 'viewed',
              impression: 'restaurant',
            }}
            style={{ height: '100%' }}>
            {renderCard(restaurant, index)}
          </TrackProvider>
        ),
      }))}
    />
  );
}

export type RestaurantCarouselSkeletonProps = {
  heading: ReactNode;
  seeAll: ReactNode;
  renderCard: () => ReactNode;
  itemCount?: number;
  headerClassName?: string;
  headingClassName?: string;
  trackClassName?: string;
  itemClassName?: string;
  className?: string;
  id?: string;
};

export function RestaurantCarouselSkeleton({
  heading,
  seeAll,
  renderCard,
  itemCount = 4,
  ...frameProps
}: RestaurantCarouselSkeletonProps) {
  return (
    <RestaurantCarouselFrame
      {...frameProps}
      heading={heading}
      seeAll={seeAll}
      scrollable={false}
      items={Array.from({ length: itemCount }, (_, index) => ({
        key: index,
        node: renderCard(),
      }))}
    />
  );
}

export function CityCarouselSeeAll({
  onClick,
  disabled = false,
}: {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
}) {
  return (
    <Button
      kind="link"
      onClick={onClick}
      disabled={disabled}
      aria-hidden={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={cn(
        'shrink-0 text-base font-medium text-ds-brand-700',
        disabled && 'pointer-events-none'
      )}>
      See all
    </Button>
  );
}

export type FilteredRestaurantsCarouselProps = {
  restaurants: FilteredRestaurant[];
  heading: string;
  className?: string;
  id?: string;
  onSeeAllClick?: MouseEventHandler<HTMLButtonElement>;
};

export default function FilteredRestaurantsCarousel({
  restaurants,
  heading,
  onSeeAllClick,
  ...frameProps
}: FilteredRestaurantsCarouselProps) {
  return (
    <RestaurantCarousel
      {...frameProps}
      restaurants={restaurants}
      heading={heading}
      seeAll={<CityCarouselSeeAll onClick={onSeeAllClick} />}
      renderCard={(restaurant, index) => (
        <FilteredRestaurantCarouselCard
          restaurant={restaurant}
          imagePriority={index < 2}
          className="h-full"
        />
      )}
    />
  );
}
